import { useEffect, useRef } from 'react';

/**
 * useAutoRefresh — re-runs the given async fetch function whenever:
 *   1. The browser tab regains visibility (`visibilitychange` → visible)
 *   2. The window receives focus
 *   3. The browser comes back online
 *
 * Throttled to once every `throttleMs` ms (default 2000) so rapid tab-switches
 * don't hammer the API.
 *
 * Use this on dashboard pages so admin/center/student panels stay live without
 * manual F5. Pair with the existing useEffect that loads initial data.
 *
 * @example
 *   const fetchCourses = useCallback(async () => { ... }, []);
 *   useEffect(() => { fetchCourses(); }, [fetchCourses]);
 *   useAutoRefresh(fetchCourses);   // ← that's it
 */
export function useAutoRefresh(fetchFn, { throttleMs = 2000, enabled = true } = {}) {
  const lastRunRef = useRef(0);
  const fnRef = useRef(fetchFn);

  // Keep the latest function reference without forcing re-binding the listener
  useEffect(() => {
    fnRef.current = fetchFn;
  }, [fetchFn]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;

    const run = () => {
      const now = Date.now();
      if (now - lastRunRef.current < throttleMs) return;
      lastRunRef.current = now;
      try {
        const out = fnRef.current && fnRef.current();
        if (out && typeof out.catch === 'function') out.catch(() => {});
      } catch (_e) {
        // swallow — never let an auto-refresh kill the page
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') run();
    };

    window.addEventListener('focus', run);
    window.addEventListener('online', run);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('focus', run);
      window.removeEventListener('online', run);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, throttleMs]);
}

export default useAutoRefresh;
